import { AllAboardAPI, AuthAPI, CustomerAPI, MeetingsAPI } from 'api'
import config from 'config'

import { GoogleScopes } from '../components/constants/settings'

// User
export const fetch = (userId) => {
  return CustomerAPI.get(`/users/${userId}`).then((response) => response.data)
}

export const fetchAll = () =>
  CustomerAPI.get('/users/getAll').then((response) => response.data)

export const fetchAllByStatus = (status) =>
  CustomerAPI.get(`/users/getAll?status=${status}`)

export const update = (userId, updates) => {
  return CustomerAPI.patch(`/users/${userId}`, updates).then(
    (response) => response.data
  )
}

export const updateUser = (user) => {
  return CustomerAPI.patch(`/users/v2/${user.id}`, user).then(
    (response) => response.data
  )
}

export const fetchGoogleScopes = () => {
  return MeetingsAPI.get('/google/calendar/oauth/scopes').then(
    (response) => response.data
  )
}

export const getIsGoogleCalendarAuthed = () => {
  fetchGoogleScopes().then((res) => res.includes(GoogleScopes.CALENDAR))
}

export const getIsGmailAuthed = () => {
  return fetchGoogleScopes().then(
    (res) => res.includes(GoogleScopes.READ) && res.includes(GoogleScopes.SEND)
  )
}

export const disconnectGmailAccount = () => {
  return MeetingsAPI.post('/google/calendar/disconnect')
}

// TODO - include basic microsoft auth requests

// User Properties
export const fetchUserProperties = (userId) => {
  return CustomerAPI.get(`/users/${userId}/properties`).then(
    (response) => response.data
  )
}

export const patchUserProperties = (userId, patchOperation) => {
  return CustomerAPI.patch(`/users/${userId}/properties`, patchOperation).then(
    (response) => response.data
  )
}

const patchUserPropertiesV2 = (userId, patchOperation) => {
  return CustomerAPI.patch(`/users/${userId}/properties2`, patchOperation).then(
    (response) => response.data
  )
}

export const updateUserJobTitle = (userId, patchOperation) => {
  return AllAboardAPI.patch(`/job-tasks/${userId}`, patchOperation)
}

export const fetchProfileSettings = (userId) => {
  return CustomerAPI.get(`/users/${userId}/profile`).then(
    (response) => response.data
  )
}

export const patchProfileSettings = (userId, newProfileSettings) => {
  return CustomerAPI.patch(`/users/${userId}/profile`, newProfileSettings).then(
    (response) => response.data
  )
}

export const patchUserPropertiesV2WithOperation = (userId, property, value) => {
  const patchOperation = [
    { property: property, operation: 'set', value: value },
  ]
  return patchUserPropertiesV2(userId, patchOperation)
}

export const batchPatchUserPropertiesV2WithOperation = (
  userId,
  propertyValuePairs
) => {
  const patchOperation = propertyValuePairs.map((item) => ({
    ...item,
    operation: 'set',
  }))
  return patchUserPropertiesV2(userId, patchOperation)
}

export const uploadUserAvatar = (userId, file) => {
  const data = new FormData()
  data.append('file', file)

  return CustomerAPI.post(`/users/${userId}/avatar`, data, {
    file: true,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }).then((response) => response.data)
}

export const clearUserAvatar = () =>
  CustomerAPI.delete('/users/avatar').then((response) => response.data)

export const changePassword = (oldPassword, newPassword) => {
  const passwordUpdatePayload = {
    newPassword,
    oldPassword,
  }
  return AuthAPI.post(
    `/password/update?clientId=${config.AUTH_CLIENT_ID}&redirectUrl=${config.APP_URL}/settings2/account`,
    passwordUpdatePayload
  ).then((response) => response.data)
}

export const archiveUsers = ({ orgId, userIds }) => {
  return CustomerAPI.delete(`/organizations/${orgId}/users`, {
    data: userIds,
  }).then((response) => response.data)
}

export const transferOwner = ({ orgId, id }) => {
  return CustomerAPI.post(
    `/organizations/${orgId}/users/change_owner`,
    id
  ).then((response) => response.data)
}

export const getInviteRequests = ({ orgId, requestSource }) => {
  return CustomerAPI.get(
    `/inviteRequest/${orgId}?requestSource=${requestSource}`
  ).then((response) => response.data)
}

export const deleteInvite = ({ orgId, email, requestSource }) => {
  return CustomerAPI.delete(`/inviteRequest/${orgId}`, {
    data: { orgId, email, requestSource },
  })
}

export const deleteInviteBulk = (orgId, inviteRequests) => {
  return CustomerAPI.delete(`/inviteRequest/${orgId}/bulk`, {
    data: inviteRequests,
  })
}

export const resendInvite = ({ orgId, emails }) => {
  return CustomerAPI.post(
    `/organizations/${orgId}/users/invitations/reinvite`,
    emails
  ).then((response) => response.data)
}

export const resendPendingInvite = (orgId, email) => {
  return CustomerAPI.post(`/organizations/${orgId}/users/reinvite/existing`, {
    email,
  }).then((response) => response.data)
}

export const reinviteWithLink = ({ orgId, emails }) => {
  return CustomerAPI.post(
    `/organizations/${orgId}/users/invitations/links`,
    emails
  ).then((response) => response.data)
}

export const reinvitePendingUserWithLink = (orgId, email) => {
  return CustomerAPI.post(
    `/organizations/${orgId}/users/invitations/pending_link`,
    { email }
  ).then((response) => response.data)
}

export const fetchConnectedCalendars = () => {
  return MeetingsAPI.get('/scheduling/settings/connected').then(
    (response) => response.data
  )
}

export const fetchOutOfOffice = () => {
  return MeetingsAPI.get('/scheduling/settings/ooo_agents').then(
    (response) => response.data
  )
}

export const setUserOutOfOfficeTime = (payload) => {
  return MeetingsAPI.post(
    '/calendar/properties/update_ooo/admin',
    payload
  ).then((response) => response.data)
}

export const getConvoLimit = (userId) => {
  return CustomerAPI.get(`/users/${userId}/convo-limit`).then(
    (response) => response.data
  )
}

export const setConvoLimit = (userId, limit, remove) => {
  return CustomerAPI.post(`/users/${userId}/convo-limit`, {
    limit,
    remove,
  }).then((response) => response.data)
}

export const setAvailability = (userId, availability) =>
  CustomerAPI.patch(
    `/users/${userId}/availability?status=${availability}`
  ).then((response) => response.data)

export const fetchOrgMultiSignature = (userId) => {
  return CustomerAPI.get(`/users/${userId}/email_signature/all`).then(
    (response) => response.data
  )
}

export const fetchEmailSignature = (userId) => {
  return CustomerAPI.get(`/users/${userId}/email_signature`).then(
    (response) => response.data
  )
}

export const setEmailSignature = (userId, emailSignature) => {
  return CustomerAPI.post(`/users/${userId}/email_signature`, {
    emailSignature,
  }).then((response) => response.data)
}

export const resetEmailSignature = (userId) => {
  return CustomerAPI.post(`/users/${userId}/email_signature`, {
    default: true,
  }).then((response) => response.data)
}

export const isUserHandleTaken = (globalHandle) => {
  return CustomerAPI.head(`/users/handle_check?globalHandle=${globalHandle}`)
    .then((response) => response.status)
    .catch(() => 404)
}

export const requestEmailChange = (email) => {
  return CustomerAPI.patch('/users/email', email)
}

export const fetchEmailPendingVerification = () => {
  return CustomerAPI.get('/email/check-update')
}

export const resendVerificationEmail = () =>
  CustomerAPI.post('users/email-resend')

export const getUsersAvailability = (userIds) =>
  CustomerAPI.post('/users/availability', userIds)

export const getUserProperties = async (id) =>
  CustomerAPI.get(`organizations/${id}/end_users/attributes2`, {
    params: { playbookEventInclude: false },
  })
